import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import { PageHeader } from '../../components/PageHeader';
import Select from '../../components/Select';
import { notificationActions } from '../../components/Notification/redux/actions';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { openModalAction } from '../../actions/modal';
import { isCanceledResponse } from '../../middleware/api';
import { DATE_MONTH_DAY_YEAR } from '../../constants';
import { useGlobalPageSize } from '../../utils/tableUtils';
import { toolTipForTable } from '../../utils';
import { isOrganizationManager } from '../../utils/userRoles';
import Strings from '../../Strings';
import {
  ACCESS_ACTION_TYPES,
  ACCESS_TRACKS,
  actions,
  EDIT_PATIENT_ACTION_MODAL_NAME,
  errorMessage,
  LIST_PATIENT_ACTIONS_RESULT,
  PATCH_PATIENT_ACTION_RESULT,
  priorityLabel,
  SEARCH_ACTION_ASSIGNEES_RESULT,
} from './actions';

import './ActionsPage.scss';

const CONTENT_PREVIEW_LENGTH = 80;

const priorityCell = priority => {
  const label = priorityLabel(priority);
  const icon = { high: 'alert', medium: 'warning' }[label];
  return (
    <div className="icon-and-text-container">
      {icon && <div className={`icon-container ${icon}`} />}
      {Strings.patientActions[label]}
    </div>
  );
};

const stateCell = completed => (
  <div className="icon-and-text-container">
    <div className={`icon-container ${completed ? 'tick' : 'pending'}`} />
    {completed ? Strings.patientActions.completed : Strings.patientActions.open}
  </div>
);

const deadlineCell = row => {
  if (!row.due_at) return '-';
  const date = moment(row.due_at).format(DATE_MONTH_DAY_YEAR);
  return row.past_deadline && !row.action_completed
    ? <span className="patient-actions__overdue">{`${date} (${Strings.patientActions.overdue})`}</span>
    : date;
};

function ActionsTable({ listKey, query, refreshKey, columns, buttons, onRowClick, listActions, showNotification }) {
  const globalPageSize = useGlobalPageSize();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const page = useRef({ offset: 0, limit: globalPageSize });

  const load = useCallback(() => {
    setIsLoading(true);
    const params = { ...query, offset: page.current.offset, limit: page.current.limit };
    if (page.current.sortColumn) {
      params.sortColumn = page.current.sortColumn;
      params.sortType = page.current.sortType;
    }
    listActions(listKey, params).then(response => {
      if (isCanceledResponse(response)) return;
      if (response?.type === LIST_PATIENT_ACTIONS_RESULT) {
        setRows(response.response?.data || []);
        setPagination({ ...response.response?.pagination, offset: page.current.offset, limit: page.current.limit });
      } else {
        setRows([]);
        showNotification(errorMessage(response, Strings.patientActions.requestFailed), 5000, true);
      }
      setIsLoading(false);
    });
  }, [listKey, query, listActions, showNotification]);

  useEffect(() => {
    page.current.offset = 0;
    load();
  }, [load, refreshKey]);

  return (
    <Table3
      name={`patient-actions-${listKey}`}
      data={rows}
      columns={columns}
      buttons={buttons}
      isLoading={isLoading}
      pagination={pagination}
      onRowClick={onRowClick}
      onOffsetChange={offset => {
        page.current.offset = offset;
        load();
      }}
      onPageSizeChange={limit => {
        page.current.limit = limit;
        page.current.offset = 0;
        load();
      }}
      onSortingChange={sorting => {
        page.current.sortColumn = sorting?.[0]?.id;
        page.current.sortType = sorting?.[0] ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
        page.current.offset = 0;
        load();
      }}
      enableColumnFiltering
      pinFirstColumn
    />
  );
}

ActionsTable.propTypes = {
  listKey: PropTypes.string.isRequired,
  query: PropTypes.object.isRequired,
  refreshKey: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  buttons: PropTypes.array,
  onRowClick: PropTypes.func,
  listActions: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

function ActionsPage({ profileUuid, orgIds, listActions, patchAction, searchAssignees, openEdit, onNavigate, showNotification }) {
  const text = Strings.patientActions;
  // Only users the server accepts as assignees (doctors) own actions: the
  // assignee search with include_self lists the caller exactly then.
  const [canOwnActions, setCanOwnActions] = useState(false);
  // null until that is known, so the first list loads once, with the right assignee.
  const [assignee, setAssignee] = useState(null);
  const [status, setStatus] = useState('open');
  const [priority, setPriority] = useState('');
  const [view, setView] = useState('');
  const [accessType, setAccessType] = useState('');
  const [track, setTrack] = useState('');
  const [users, setUsers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  useEffect(() => {
    searchAssignees().then(response => {
      if (isCanceledResponse(response)) return;
      const found = response?.type === SEARCH_ACTION_ASSIGNEES_RESULT ? response.response?.data || [] : [];
      const canOwn = found.some(user => user.uuid === profileUuid);
      setCanOwnActions(canOwn);
      setAssignee(canOwn ? 'me' : 'assigned');
      setUsers(found.filter(user => user.uuid !== profileUuid));
    });
  }, [searchAssignees, profileUuid]);

  const filterQuery = useMemo(() => {
    const query = { status };
    if (priority !== '') query.priority = priority;
    if (view === 'overdue') query.overdue = true;
    if (view === 'hidden') query.hidden = true;
    if (accessType !== '') query.action_type = accessType;
    if (track !== '') query.track = track;
    if (orgIds?.length > 0) query.orgId = orgIds;
    return query;
  }, [status, priority, view, accessType, track, orgIds]);
  const assignedQuery = useMemo(() => ({ ...filterQuery, assignee }), [filterQuery, assignee]);
  const unassignedQuery = useMemo(() => ({ ...filterQuery, assignee: 'none' }), [filterQuery]);

  const onAssignToMe = (_, row) => {
    patchAction(row.id, { assignee: profileUuid }).then(response => {
      if (response?.type === PATCH_PATIENT_ACTION_RESULT) {
        refresh();
      } else {
        showNotification(errorMessage(response, text.requestFailed), 5000, true);
      }
    });
  };
  const onEdit = (_, row) => openEdit({ action: row, afterSave: refresh });

  const columns = useMemo(() => [
    columnHelper.accessor('priority', { header: text.priority, cell: info => priorityCell(info.getValue()), size: 130 }),
    columnHelper.accessor('action_completed', {
      header: text.state,
      cell: info => stateCell(info.getValue()),
      size: 140,
      enableSorting: false,
    }),
    columnHelper.accessor('access_action_type', {
      header: text.type,
      cell: info => text.accessTypes[info.getValue()] || '-',
      size: 220,
      enableSorting: false,
    }),
    columnHelper.accessor('content', {
      header: text.action,
      cell: info => {
        const content = info.getValue() || '';
        return content.length > CONTENT_PREVIEW_LENGTH
          ? toolTipForTable(`${content.slice(0, CONTENT_PREVIEW_LENGTH)}…`, content)
          : content || '-';
      },
      size: 420,
      enableSorting: false,
    }),
    columnHelper.accessor('patient', {
      header: text.patient,
      accessorFn: row => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
      size: 200,
    }),
    columnHelper.accessor('assignee', {
      header: text.assignee,
      cell: info => info.getValue()?.name || '-',
      size: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('due_at', { header: text.deadline, cell: info => deadlineCell(info.row.original), size: 230 }),
  ], [text]);

  const buttons = [
    { icon: 'edit', text: text.edit, testId: 'edit-action', onClick: onEdit },
    {
      icon: 'enroll',
      text: text.assignToMe,
      testId: 'assign-to-me',
      onClick: onAssignToMe,
      visible: (_, row) => canOwnActions && !row.action_completed && row.assignee?.uuid !== profileUuid,
    },
  ];
  // Organization managers cannot open patients.
  const onRowClick = !isOrganizationManager()
    ? row => row?.patient_id && onNavigate(`/cap-patients/${encodeURIComponent(row.patient_id)}`)
    : undefined;

  const assigneeOptions = [
    ...(canOwnActions ? [{ value: 'me', label: text.me }] : []),
    { value: 'assigned', label: text.anyone },
    ...users.map(user => ({ value: user.uuid, label: `${user.first_name} ${user.last_name}` })),
  ];
  const filters = (
    <div className="patient-actions__filters">
      <Select
        id="patient-actions-status"
        label={text.state}
        value={status}
        data={[{ value: 'open', label: text.open }, { value: 'completed', label: text.completed }, { value: 'any', label: text.allStatuses }]}
        onChange={option => setStatus(option.value)}
      />
      <Select
        id="patient-actions-priority"
        label={text.priority}
        value={priority}
        data={[{ value: '', label: text.allPriorities }, { value: 2, label: text.high }, { value: 1, label: text.medium }, { value: 0, label: text.low }]}
        onChange={option => setPriority(option.value)}
      />
      <Select
        id="patient-actions-view"
        label={text.hide}
        value={view}
        data={[{ value: '', label: text.notHidden }, { value: 'overdue', label: text.overdueOnly }, { value: 'hidden', label: text.showHidden }]}
        onChange={option => setView(option.value)}
      />
      <Select
        id="patient-actions-access-type"
        label={text.accessType}
        value={accessType}
        data={[
          { value: '', label: text.allAccessTypes },
          ...ACCESS_ACTION_TYPES.map(type => ({ value: type, label: text.accessTypes[type] })),
        ]}
        onChange={option => setAccessType(option.value)}
      />
      <Select
        id="patient-actions-track"
        label={text.track}
        value={track}
        data={[{ value: '', label: text.allTracks }, ...ACCESS_TRACKS.map(value => ({ value, label: value }))]}
        onChange={option => setTrack(option.value)}
      />
    </div>
  );

  return (
    <div className="patient-actions">
      <PageHeader left={text.title} right={filters} />
      <section className="patient-actions__list" data-testid="patient-actions-assigned">
        <PageHeader
          left={text.assignedTo}
          right={(
            <Select
              id="patient-actions-assignee"
              label={text.assignedTo}
              value={assignee}
              data={assigneeOptions}
              isSearchable
              onChange={option => setAssignee(option.value)}
            />
          )}
        />
        {assignee !== null && (
          <ActionsTable
            listKey="assigned"
            query={assignedQuery}
            refreshKey={refreshKey}
            columns={columns}
            buttons={buttons}
            onRowClick={onRowClick}
            listActions={listActions}
            showNotification={showNotification}
          />
        )}
      </section>
      <section className="patient-actions__list" data-testid="patient-actions-unassigned">
        <PageHeader left={text.unassigned} />
        <ActionsTable
          listKey="unassigned"
          query={unassignedQuery}
          refreshKey={refreshKey}
          columns={columns}
          buttons={buttons}
          onRowClick={onRowClick}
          listActions={listActions}
          showNotification={showNotification}
        />
      </section>
    </div>
  );
}

ActionsPage.propTypes = {
  profileUuid: PropTypes.string,
  orgIds: PropTypes.array,
  listActions: PropTypes.func.isRequired,
  patchAction: PropTypes.func.isRequired,
  searchAssignees: PropTypes.func.isRequired,
  openEdit: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  profileUuid: state.auth.profile?.uuid,
  orgIds: state.entities?.orgFilter?.orgIds,
});

const mapDispatchToProps = dispatch => ({
  listActions: (listKey, params) => dispatch(actions.listActions(listKey, params)),
  patchAction: (noteId, data) => dispatch(actions.patchAction(noteId, data)),
  searchAssignees: () => dispatch(actions.searchAssignees()),
  openEdit: data => dispatch(openModalAction(EDIT_PATIENT_ACTION_MODAL_NAME, data)),
  onNavigate: path => dispatch(push(path)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActionsPage);
